import { supabase } from './supabase/client';
import { Badge, CreateBadgeDTO, UpdateBadgeDTO, BadgeFilters, BadgeListResponse } from 'src/types/teacher.types';
import { Tables } from 'src/types/db.types';

const BADGES_TABLE = 'badges';
const TABLE_NAME = 'students';

/**
 * Service for managing teacher data
 */
export const badgeService = {
  async getBadges({ search, page = 1, limit = 100 }: BadgeFilters = {}): Promise<BadgeListResponse> {
    try {
      let query = supabase.from(BADGES_TABLE).select('*', { count: 'exact' });

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdat', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data as Badge[],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[TeacherService.getBadges]:', error);
      throw error;
    }
  },

  /**
   * Get a single badge by ID
   */
  async getBadgeById(id: string): Promise<Badge | null> {
    try {
      const { data, error } = await supabase.from(BADGES_TABLE).select('*').eq('id', id).single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[TeacherService.getBadgeById]:', error);
      throw error;
    }
  },

  /**
   * Create a new badge
   */
  async createBadge(badge: CreateBadgeDTO): Promise<Badge> {
    try {
      const { data, error } = await supabase.from(BADGES_TABLE).insert([badge]).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[TeacherService.createBadge]:', error);
      throw error;
    }
  },

  /**
   * Update an existing badge
   */
  async updateBadge(id: string, updates: UpdateBadgeDTO): Promise<Badge> {
    try {
      const { data, error } = await supabase.from(BADGES_TABLE).update(updates).eq('id', id).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[TeacherService.updateBadge]:', error);
      throw error;
    }
  },

  /**
   * Delete a badge
   */
  async deleteBadge(id: string): Promise<void> {
    try {
      const { error } = await supabase.from(BADGES_TABLE).delete().eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[TeacherService.deleteBadge]:', error);
      throw error;
    }
  },

  /**
   * Update badge image
   */
  async updateBadgeImage(id: string, file: File): Promise<string> {
    try {
      // Upload image to storage
      let { data, error } = await supabase.storage
        .from('badge-images')
        .upload(`public/${id}/${file.name}`, file, { upsert: true });

      if (error && error.statusCode === '409') {
        await supabase.storage.from('badge-images').remove([`public/${id}/${file.name}`]);
        const retry = await supabase.storage.from('badge-images').upload(`public/${id}/${file.name}`, file);
        if (retry.error) throw retry.error;
        data = retry.data;
      }

      if (error) throw error;

      // Get public URL
      const publicUrl = supabase.storage.from('badge-images').getPublicUrl(data.path).data.publicUrl;

      // Update badge with new image URL
      const { error: updateError } = await supabase.from(BADGES_TABLE).update({ imageUrl: publicUrl }).eq('id', id);

      if (updateError) throw updateError;

      return publicUrl;
    } catch (error) {
      console.error('[TeacherService.updateBadgeImage]:', error);
      throw error;
    }
  },

  /**
   * Delete badge image
   */
  async deleteBadgeImage(id: string): Promise<void> {
    try {
      const { data: badge } = await supabase.from(BADGES_TABLE).select('imageUrl').eq('id', id).single();

      if (badge?.imageUrl) {
        const path = badge.imageUrl.split('/').slice(-2).join('/');
        const { error: deleteError } = await supabase.storage.from('badge-images').remove([path]);

        if (deleteError) throw deleteError;

        const { error: updateError } = await supabase.from(BADGES_TABLE).update({ imageUrl: null }).eq('id', id);

        if (updateError) throw updateError;
      }
    } catch (error) {
      console.error('[TeacherService.deleteBadgeImage]:', error);
      throw error;
    }
  },

  /**
   * Assign a badge to a student
   */
  async assignBadge(badgeId: string, studentId: string): Promise<void> {
    try {
      const { error } = await supabase.from('students').update({ badgeid: badgeId }).eq('id', studentId);

      if (error) throw error;
    } catch (error) {
      console.error('[BadgeService.assignBadge]:', error);
      throw error;
    }
  },
};
